import React, { useCallback, useMemo } from "react";
import classnames from "classnames";
import { FormGroup, Intent, Checkbox, Switch, Alignment } from "@blueprintjs/core";
//import FaIcon from "../../FaIcon";
import { IRadioFieldProps, ICheckboxGroupProps, FieldOptionType } from "../models";
import { colCountsToDeviceClasses } from "../models/utils";
import { FastField, FieldProps, getIn } from "formik";
import RequiredIndicator from "../../../shared/RequiredIndicator";
import { validationMethodFactory, hasRequiredValidation } from "../models/validation";
import useFormContext from "../FormContext";
import css from "../form.module.scss";
import CheckIcon from "./PrintedCheckIcon";



type  Props<T=any> = FieldProps<T> & { fieldProps: ICheckboxGroupProps, className: string };


const BoundField = ({ field, form, fieldProps }: Props) => {
  const { isEditing, isPrinting, fieldChangedSignal } = useFormContext();
  const error = (form.submitCount || getIn(form.touched, field.name)) && getIn(form.errors, field.name);
  const intent = error ? Intent.DANGER : Intent.NONE;
  const values: FieldOptionType[] = useMemo(() => field.value || [], [field.value]);
  const isRequired = hasRequiredValidation(fieldProps.validations);
  const disabled = fieldProps.disabled ?? false;
  const multiSelect = fieldProps.multiSelect ?? true;
  const textAlignment = fieldProps.textAlignment ?? Alignment.LEFT;

  const toggle = useCallback((value: FieldOptionType) => {
    const currentValues = [...values];
    const index = values.indexOf(value);

    (!multiSelect) && (values.length = 0);
    (index >= 0 ? values.splice(index, 1) : values.push(value))

    // Must assign a new array in order for Formik change detection to work
    const newValues = [...values];
    form.setFieldValue(field.name, newValues);
    form.setFieldTouched(field.name, !values, !values);  // It is not flagged as touched or validated when values is empty.

    fieldChangedSignal.dispatch(field.name, values, currentValues);

  }, [field.name, fieldChangedSignal, form, multiSelect, values]);

  const itemClasses = useMemo(() =>
    classnames("column", colCountsToDeviceClasses({mobileCols: fieldProps.mobileCols, tabletCols: fieldProps.tabletCols, desktopCols: fieldProps.desktopCols}))
  , [fieldProps.mobileCols, fieldProps.tabletCols, fieldProps.desktopCols]);

  const items = useMemo(() => (fieldProps.options || []).map(option => {
    const toggleItem = () => toggle(option.value);
    const label = option.text || option.value.toString();
    const isChecked = !!values.find(v => v === option.value);
    const itemKey = option.value.toString();

    if (isPrinting) {
      return (<CheckIcon key={itemKey} isChecked={isChecked} label={label} className={itemClasses} />)
    }

    const itemProps = {
      key: itemKey,
      className: itemClasses,
      disabled: !isEditing || disabled,
      checked: isChecked,
      onChange: toggleItem,
      alignIndicator: textAlignment
    }

    return fieldProps.useAltStyle === true ?
      <Switch {...itemProps}>{label}</Switch>
      : <Checkbox {...itemProps}>{label}</Checkbox>;
  }), [fieldProps.options, fieldProps.useAltStyle, values, isPrinting, itemClasses, isEditing, disabled, textAlignment, toggle]);

  const className = classnames(css.formPart, css.checkboxGroup);

  return (
    <FormGroup
      label={fieldProps.label}
      labelInfo={<RequiredIndicator show={isRequired} isPrinting={isPrinting} />}
      intent={intent}
      helperText={isEditing ? (error || <>&nbsp;</>) : undefined}
      style={fieldProps.style}
      className={className}
    >
      <div className={classnames("columns is-mobile is-multiline", css.checkboxItems, error && css.error)}>
        {items}
      </div>
    </FormGroup>)
}


const CheckboxGroupField = (props : IRadioFieldProps) => {
  console.assert(props && props.binding);
  const { isEditing } = useFormContext();
  const validate = isEditing ? validationMethodFactory(props) : undefined;
  return <FastField name={props.binding} fieldProps={props} component={BoundField} validate={validate} />;
}


export default CheckboxGroupField;






